import * as THREE from "three";
import { CHUNK_SIZE, COLUMN_HEIGHT, WORLD_EXTENT } from "../../sim/world/constants";
import { PaletteSubChunk } from "../../sim/world/palette";
import type { BlockRegistry, ChunkPos } from "../../sim/world/types";
import { chunkKey, VoxelWorld, worldToChunk } from "../../sim/world/world";
import { MeshWorkerPool } from "../../workers/MeshWorkerPool";
import { ChunkCuller, type CullableChunk, type CullingStats } from "../culling/ChunkCuller";
import { BlockMaterialSet, geometryFromPackedMesh, meshDefinitionsFromRegistry } from "../materials/BlockMaterial";
import { buildMeshingInput } from "../meshing/neighborhood";
import { RENDER_GROUPS, type BlockMeshDef, type MeshingResult } from "../meshing/types";

export interface ChunkLoader {
  load(position: ChunkPos): Promise<PaletteSubChunk> | PaletteSubChunk;
  cancel?(position: ChunkPos): void;
  unload?(position: ChunkPos, chunk: PaletteSubChunk): void;
}

export interface ChunkStreamerOptions {
  readonly radius?: number;
  /**
   * Sub-chunks above and below the camera to consider, in chunks. Defaults to
   * `radius`, which makes the resident set a sphere.
   *
   * This exists because the streamer used to resident every sub-chunk in a
   * column whatever the camera height. At eight sub-chunks per column that was
   * merely wasteful; the micro-voxel rescale took columns to 64, which put the
   * resident set at roughly 29,000 chunks at radius 12 — not a tuning problem
   * but an unshippable one. Lowering this below `radius` trades vertical reach
   * for chunk count, which is usually the right trade above ground.
   */
  readonly verticalRadius?: number;
  readonly loadsPerFrame?: number;
  /**
   * Ceiling on loads in flight at once. Defaults to 32 — roughly a few chunks
   * of work per generator worker, enough to keep them busy without letting the
   * streamer's queue migrate into the loader where it cannot be cancelled.
   */
  readonly maxConcurrentLoads?: number;
  readonly meshesPerFrame?: number;
  readonly uploadsPerFrame?: number;
  readonly uploadBudgetMs?: number;
  readonly definitions?: readonly BlockMeshDef[];
  readonly onError?: (error: Error, stage: "load" | "mesh", position: ChunkPos) => void;
}

/** Initial attempt plus three transient retries before a load is unrecovered. */
export const MAX_LOAD_ATTEMPTS = 4;
/** Initial attempt plus two transient retries before a mesh is unrecovered. */
export const MAX_MESH_ATTEMPTS = 3;

/** True when the rejecting layer marked the failure unrecoverable (stopped pool/loader). */
export function isPermanentChunkError(error: unknown): boolean {
  return typeof error === "object" && error !== null && (error as { permanent?: unknown }).permanent === true;
}

/**
 * The first retry runs on the next update (the streaming contract for quick
 * transient blips); later retries back off exponentially so a sick loader is
 * not hammered every frame.
 */
function loadBackoffFrames(attempt: number): number {
  return attempt <= 1 ? 0 : Math.min(2 ** attempt, 16);
}

interface ResidentChunk extends CullableChunk {
  chunk: PaletteSubChunk;
  object: THREE.Group;
  connectivity: Uint8Array;
  drawCallCount: number;
  meshVersion: number;
}

interface LoadRequest {
  readonly position: ChunkPos;
  readonly distance: number;
}

interface UploadRequest {
  readonly key: string;
  readonly version: number;
  readonly result: MeshingResult;
}

/** Ring-prioritized column streaming with strict load, mesh-start and upload budgets. */
export class ChunkStreamer {
  readonly culler: ChunkCuller;
  private readonly radius: number;
  private readonly verticalRadius: number;
  private readonly loadsPerFrame: number;
  private readonly maxConcurrentLoads: number;
  private readonly meshesPerFrame: number;
  private readonly uploadsPerFrame: number;
  private readonly uploadBudgetMs: number;
  private readonly definitions: readonly BlockMeshDef[];
  private readonly onError: ChunkStreamerOptions["onError"];
  private readonly resident = new Map<string, ResidentChunk>();
  private readonly desired = new Set<string>();
  private readonly loading = new Set<string>();
  private readonly loadQueue: LoadRequest[] = [];
  private readonly remeshQueue: string[] = [];
  private readonly remeshQueued = new Set<string>();
  private readonly uploadQueue: UploadRequest[] = [];
  private centerCx = Number.NaN;
  private centerCy = Number.NaN;
  private centerCz = Number.NaN;
  private disposed = false;
  private frame = 0;
  private readonly loadAttempts = new Map<string, number>();
  private readonly loadRetryFrame = new Map<string, number>();
  private readonly failedLoads = new Set<string>();
  private readonly meshAttempts = new Map<string, number>();
  private readonly unsubscribe: () => void;

  constructor(
    private readonly world: VoxelWorld,
    registry: BlockRegistry,
    private readonly scene: THREE.Scene,
    private readonly materials: BlockMaterialSet,
    private readonly workers: MeshWorkerPool,
    private readonly loader: ChunkLoader,
    options: ChunkStreamerOptions = {},
    culler = new ChunkCuller(),
  ) {
    this.radius = options.radius ?? 12;
    this.verticalRadius = options.verticalRadius ?? this.radius;
    this.loadsPerFrame = options.loadsPerFrame ?? 2;
    this.maxConcurrentLoads = options.maxConcurrentLoads ?? 32;
    this.meshesPerFrame = options.meshesPerFrame ?? 1;
    this.uploadsPerFrame = options.uploadsPerFrame ?? 2;
    this.uploadBudgetMs = options.uploadBudgetMs ?? 4;
    this.definitions = options.definitions ?? meshDefinitionsFromRegistry(registry);
    this.onError = options.onError;
    this.culler = culler;
    this.unsubscribe = world.subscribe((dirty) => {
      // World guarantees at most the edited chunk plus six touched neighbours.
      for (const position of dirty) this.queueRemesh(chunkKey(position.cx, position.cy, position.cz));
    });
  }

  get residentCount(): number {
    return this.resident.size;
  }

  get queuedUploads(): number {
    return this.uploadQueue.length;
  }

  get loadingCount(): number {
    return this.loading.size;
  }

  get queuedLoads(): number {
    return this.loadQueue.length;
  }

  get queuedMeshes(): number {
    return this.remeshQueue.length;
  }

  /** Chunks the current ring wants resident; the capture completeness denominator. */
  get desiredCount(): number {
    return this.desired.size;
  }

  /** Current culling stats without advancing the load/mesh/upload pipeline. */
  visibility(camera: THREE.Camera): CullingStats {
    if (this.disposed) throw new Error("chunk streamer is disposed");
    return this.culler.cull(camera, this.resident.values());
  }

  /** Call once per animation frame. Upload work is capped by count and elapsed time. */
  update(camera: THREE.Camera): CullingStats {
    if (this.disposed) throw new Error("chunk streamer is disposed");
    this.frame += 1;
    const cameraPosition = new THREE.Vector3();
    camera.getWorldPosition(cameraPosition);
    const cx = worldToChunk(cameraPosition.x);
    const cy = worldToChunk(cameraPosition.y);
    const cz = worldToChunk(cameraPosition.z);
    if (cx !== this.centerCx || cy !== this.centerCy || cz !== this.centerCz) this.rebuildRings(cx, cy, cz);
    this.startLoads();
    this.startMeshes();
    this.uploadMeshes();
    return this.culler.cull(camera, this.resident.values());
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.unsubscribe();
    for (const resident of this.resident.values()) {
      this.disposeResident(resident);
      this.world.unloadChunk(resident.position);
      this.loader.unload?.(resident.position, resident.chunk);
    }
    this.resident.clear();
    this.desired.clear();
    this.loadQueue.length = 0;
    this.remeshQueue.length = 0;
    this.remeshQueued.clear();
    this.uploadQueue.length = 0;
    this.loadAttempts.clear();
    this.loadRetryFrame.clear();
    this.failedLoads.clear();
    this.meshAttempts.clear();
  }

  private rebuildRings(cx: number, cameraCy: number, cz: number): void {
    this.centerCx = cx;
    this.centerCy = cameraCy;
    this.centerCz = cz;
    const next = new Set<string>();
    const requests: LoadRequest[] = [];
    const columnWidth = WORLD_EXTENT / CHUNK_SIZE;
    for (let dz = -this.radius; dz <= this.radius; dz += 1) {
      for (let dx = -this.radius; dx <= this.radius; dx += 1) {
        const columnDistance = Math.sqrt(dx * dx + dz * dz);
        if (columnDistance > this.radius) continue;
        const requestCx = cx + dx;
        const requestCz = cz + dz;
        if (requestCx < 0 || requestCx >= columnWidth || requestCz < 0 || requestCz >= columnWidth) continue;
        const firstCy = Math.max(0, cameraCy - this.verticalRadius);
        const lastCy = Math.min(COLUMN_HEIGHT - 1, cameraCy + this.verticalRadius);
        for (let cy = firstCy; cy <= lastCy; cy += 1) {
          // Cull on true 3D distance, so a column near the edge of the disc
          // contributes only the sub-chunks level with the camera rather than
          // its full height. Without this the resident set is the disc times
          // COLUMN_HEIGHT, which the micro-voxel rescale made unshippable.
          const distance = Math.sqrt(columnDistance * columnDistance + (cy - cameraCy) ** 2);
          if (distance > this.radius) continue;
          const position = { cx: requestCx, cy, cz: requestCz };
          const key = chunkKey(position.cx, position.cy, position.cz);
          next.add(key);
          if (!this.resident.has(key) && !this.loading.has(key) && !this.failedLoads.has(key)) {
            requests.push({ position, distance });
          }
        }
      }
    }
    // Leaving the ring resets failure state, so returning later retries fresh.
    for (const key of this.failedLoads) if (!next.has(key)) this.failedLoads.delete(key);
    for (const key of this.loadAttempts.keys()) if (!next.has(key)) this.loadAttempts.delete(key);
    for (const key of this.loadRetryFrame.keys()) if (!next.has(key)) this.loadRetryFrame.delete(key);
    this.desired.clear();
    for (const key of next) this.desired.add(key);
    this.loadQueue.length = 0;
    this.loadQueue.push(...requests.sort((a, b) => a.distance - b.distance || a.position.cy - b.position.cy));

    for (const key of this.loading) {
      if (next.has(key)) continue;
      const [cx, cy, cz] = key.split(",").map(Number);
      if (cx !== undefined && cy !== undefined && cz !== undefined) this.loader.cancel?.({ cx, cy, cz });
    }

    for (const [key, resident] of this.resident) {
      if (next.has(key)) continue;
      this.disposeResident(resident);
      this.resident.delete(key);
      this.world.unloadChunk(resident.position);
      this.loader.unload?.(resident.position, resident.chunk);
      this.remeshQueued.delete(key);
      this.meshAttempts.delete(key);
      this.queueLoadedNeighbours(resident.position);
    }
  }

  private startLoads(): void {
    const deferred: LoadRequest[] = [];
    let started = 0;
    // `loadsPerFrame` caps the issue *rate*, which alone lets in-flight work
    // grow without bound: the generator produces a few hundred chunks a second
    // and this happily hands it thousands. They pile up inside the loader,
    // where the streamer can no longer reprioritise or cancel them, so a moving
    // camera starves — every request that finally lands is for a ring the
    // player already left. Holding the backlog in `loadQueue` instead keeps it
    // sorted by distance and droppable on the next rebuild.
    const capacity = Math.max(0, this.maxConcurrentLoads - this.loading.size);
    const budget = Math.min(this.loadsPerFrame, capacity);
    while (started < budget) {
      const request = this.loadQueue.shift();
      if (!request) break;
      const key = chunkKey(request.position.cx, request.position.cy, request.position.cz);
      if (!this.desired.has(key) || this.loading.has(key) || this.resident.has(key) || this.failedLoads.has(key)) {
        continue;
      }
      const retryFrame = this.loadRetryFrame.get(key);
      if (retryFrame !== undefined && retryFrame > this.frame) {
        deferred.push(request);
        continue;
      }
      started += 1;
      this.loading.add(key);
      this.loadRetryFrame.delete(key);
      void Promise.resolve().then(() => this.loader.load(request.position)).then((chunk) => {
        if (this.disposed || !this.desired.has(key)) {
          this.loader.unload?.(request.position, chunk);
          return;
        }
        let object: THREE.Group | undefined;
        try {
          if (!this.world.loadChunk(request.position, chunk)) {
            this.loader.unload?.(request.position, chunk);
            return;
          }
          object = new THREE.Group();
          object.position.set(
            request.position.cx * CHUNK_SIZE,
            request.position.cy * CHUNK_SIZE,
            request.position.cz * CHUNK_SIZE,
          );
          object.frustumCulled = false;
          this.scene.add(object);
          this.resident.set(key, {
            position: request.position,
            chunk,
            object,
            connectivity: new Uint8Array(6),
            drawCallCount: 0,
            meshVersion: 0,
          });
          this.loadAttempts.delete(key);
          this.queueRemesh(key);
          this.queueLoadedNeighbours(request.position);
        } catch (error) {
          this.resident.delete(key);
          if (object) this.scene.remove(object);
          try {
            this.world.unloadChunk(request.position);
          } finally {
            this.loader.unload?.(request.position, chunk);
          }
          throw error;
        }
      }).catch((error: unknown) => {
        // Cancelled requests leave the desired set before rejecting; they are
        // not failures and must neither count as attempts nor surface errors.
        if (this.disposed || !this.desired.has(key)) return;
        console.error("chunk load failed", request.position, error);
        const attempts = (this.loadAttempts.get(key) ?? 0) + 1;
        if (isPermanentChunkError(error) || attempts >= MAX_LOAD_ATTEMPTS) {
          this.loadAttempts.delete(key);
          this.loadRetryFrame.delete(key);
          this.failedLoads.add(key);
          this.onError?.(error instanceof Error ? error : new Error("chunk load failed"), "load", request.position);
          return;
        }
        this.loadAttempts.set(key, attempts);
        this.loadRetryFrame.set(key, this.frame + loadBackoffFrames(attempts));
        this.queueLoad(request);
      }).finally(() => {
        this.loading.delete(key);
      });
    }
    for (const request of deferred) this.queueLoad(request);
  }

  private startMeshes(): void {
    for (let count = 0; count < this.meshesPerFrame; count += 1) {
      const key = this.remeshQueue.shift();
      if (!key) return;
      this.remeshQueued.delete(key);
      const resident = this.resident.get(key);
      if (!resident) {
        count -= 1;
        continue;
      }
      const version = ++resident.meshVersion;
      const input = buildMeshingInput(this.world, resident.position, this.definitions);
      void this.workers.mesh(input).then((result) => {
        if (this.resident.get(key)?.meshVersion !== version) return;
        this.meshAttempts.delete(key);
        this.uploadQueue.push({ key, version, result });
      }).catch((error: unknown) => {
        if (this.disposed || this.resident.get(key)?.meshVersion !== version) return;
        console.error("chunk mesh failed", resident.position, error);
        const attempts = (this.meshAttempts.get(key) ?? 0) + 1;
        if (isPermanentChunkError(error) || attempts >= MAX_MESH_ATTEMPTS) {
          this.meshAttempts.delete(key);
          this.onError?.(error instanceof Error ? error : new Error("chunk mesh failed"), "mesh", resident.position);
          return;
        }
        this.meshAttempts.set(key, attempts);
        this.queueRemesh(key, true);
      });
    }
  }

  private uploadMeshes(): void {
    const started = performance.now();
    for (let count = 0; count < this.uploadsPerFrame; count += 1) {
      if (count > 0 && performance.now() - started >= this.uploadBudgetMs) return;
      const upload = this.uploadQueue.shift();
      if (!upload) return;
      const resident = this.resident.get(upload.key);
      if (!resident || resident.meshVersion !== upload.version) {
        count -= 1;
        continue;
      }
      for (const child of [...resident.object.children]) {
        resident.object.remove(child);
        if (child instanceof THREE.Mesh) child.geometry.dispose();
      }
      let drawCalls = 0;
      for (let index = 0; index < RENDER_GROUPS.length; index += 1) {
        const packed = upload.result.groups[index];
        const renderGroup = RENDER_GROUPS[index];
        if (!packed || !renderGroup || packed.indexCount === 0) continue;
        const mesh = new THREE.Mesh(geometryFromPackedMesh(packed), this.materials.material(renderGroup));
        mesh.frustumCulled = false;
        mesh.renderOrder = renderGroup === "transparent" || renderGroup === "water" ? 1 : 0;
        resident.object.add(mesh);
        drawCalls += 1;
      }
      resident.connectivity = upload.result.connectivity;
      resident.drawCallCount = drawCalls;
    }
  }

  private queueRemesh(key: string, isRetry = false): void {
    // Fresh work (edits, new residents) resets the failure budget; retries keep it.
    if (!isRetry) this.meshAttempts.delete(key);
    if (this.remeshQueued.has(key) || !this.resident.has(key)) return;
    this.remeshQueued.add(key);
    this.remeshQueue.push(key);
  }

  private queueLoad(request: LoadRequest): void {
    const key = chunkKey(request.position.cx, request.position.cy, request.position.cz);
    if (this.loadQueue.some(({ position }) => chunkKey(position.cx, position.cy, position.cz) === key)) return;
    const insertion = this.loadQueue.findIndex(({ distance }) => distance > request.distance);
    if (insertion === -1) this.loadQueue.push(request);
    else this.loadQueue.splice(insertion, 0, request);
  }

  private queueLoadedNeighbours(position: ChunkPos): void {
    for (const [dx, dy, dz] of [[1, 0, 0], [-1, 0, 0], [0, 1, 0], [0, -1, 0], [0, 0, 1], [0, 0, -1]] as const) {
      this.queueRemesh(chunkKey(position.cx + dx, position.cy + dy, position.cz + dz));
    }
  }

  private disposeResident(resident: ResidentChunk): void {
    this.scene.remove(resident.object);
    for (const child of resident.object.children) {
      if (child instanceof THREE.Mesh) child.geometry.dispose();
    }
  }
}

/** Small deterministic terrain loader useful for the dev page and smoke tests. */
export class LayeredChunkLoader implements ChunkLoader {
  load(position: ChunkPos): PaletteSubChunk {
    if (position.cy === 0) return new PaletteSubChunk(1);
    if (position.cy > 2) return new PaletteSubChunk(0);
    const chunk = new PaletteSubChunk(0);
    const baseY = position.cy * CHUNK_SIZE;
    for (let z = 0; z < CHUNK_SIZE; z += 1) {
      for (let x = 0; x < CHUNK_SIZE; x += 1) {
        const surface = 62 + Math.floor(4 * Math.sin((position.cx * CHUNK_SIZE + x) * 0.035)
          + 3 * Math.cos((position.cz * CHUNK_SIZE + z) * 0.041));
        for (let y = 0; y < CHUNK_SIZE; y += 1) {
          const worldY = baseY + y;
          if (worldY <= surface) chunk.set(x, y, z, worldY === surface ? 3 : 2);
          if (worldY <= surface) chunk.setLight(x, y, z, 0xf000);
        }
      }
    }
    return chunk;
  }
}
